import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2, Printer, Send, Trash2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { CORREO_URL } from '../../../lib/dominios';
import { esPregunta, maximoDe, puntosTotales, recorrido, secciones, varsTema } from '../../../lib/formularios';
import { useStore } from '../../../store/useStore';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import TarjetaItem from '../responder/TarjetaItem';
import { InsigniaPuntos, Retroalimentacion } from '../responder/VistaCalificacion';
import { fechaLarga, mensajeDe } from '../responder/utilidades';
import CalificarRespuesta, { puntosDe } from './CalificarRespuesta';
import '../responder/responder.css';

/*
 * Pestaña "Individual": una respuesta a la vez, pintada como el formulario ya contestado.
 * En un cuestionario, quien edita califica aquí lo que no se califica solo (párrafos, archivos…)
 * o corrige lo automático, deja comentarios y publica la calificación de esa respuesta.
 *
 * Controlada: `indice` (0 = la primera en llegar) y `onIndice` viven en la pestaña, para que la
 * vista "Pregunta" pueda saltar a una respuesta concreta.
 */

export default function VistaIndividual({ formId, formulario, puedeEditar, total, indice, onIndice, onBorrada, onImprimir }) {
  const oscuro = useStore((s) => s.theme) === 'dark';
  const [estado, setEstado] = useState({ cargando: true, respuesta: null, error: null });
  const [ediciones, setEdiciones] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [irA, setIrA] = useState('');
  const [recarga, setRecarga] = useState(0);
  const [irPendiente, setIrPendiente] = useState(null); // a qué respuesta ir si se descartan cambios

  const cargar = useCallback(async () => {
    setEstado((e) => ({ ...e, cargando: true, error: null }));
    try {
      const { data } = await api.get(`/forms/${formId}/respuestas`, { params: { desde: indice, limite: 1, orden: 'asc' } });
      setEstado({ cargando: false, respuesta: (data.items || [])[0] || null, error: null });
      setEdiciones({});
    } catch (err) {
      setEstado({ cargando: false, respuesta: null, error: mensajeDe(err, 'No se pudo cargar la respuesta') });
    }
  }, [formId, indice]);

  useEffect(() => { cargar(); }, [cargar, recarga]);

  const respuesta = estado.respuesta;
  const es = !!formulario?.ajustes?.es_cuestionario;
  const vars = useMemo(() => varsTema(formulario?.tema, { oscuro }), [formulario, oscuro]);
  const items = useMemo(() => {
    if (!respuesta) return [];
    const visitadas = recorrido(formulario, respuesta.respuestas || {});
    return secciones(formulario.items).filter((s) => visitadas.includes(s.id)).flatMap((s) => (s.item ? [s.item, ...s.items] : s.items));
  }, [formulario, respuesta]);

  const hayCambios = Object.keys(ediciones).length > 0;
  const porPregunta = respuesta?.puntuacion?.por_pregunta || {};
  const totalPuntos = items.filter(esPregunta).reduce((s, it) => s + Number(puntosDe(it, porPregunta[it.id], ediciones[it.id]) || 0), 0);
  const maximo = respuesta?.puntuacion?.maximo ?? puntosTotales(formulario?.items);

  const ir = (n, forzar = false) => {
    const destino = Math.min(Math.max(n, 0), Math.max(total - 1, 0));
    // Cambiar de respuesta con calificaciones sin guardar las perdería: se pregunta antes
    if (hayCambios && !forzar) {
      setIrPendiente(destino);
      return;
    }
    onIndice(destino);
  };

  const editar = (itemId) => (campos) => setEdiciones((e) => ({ ...e, [itemId]: { ...(e[itemId] || {}), ...campos } }));

  const guardar = async () => {
    if (!respuesta || !hayCambios) return;
    setGuardando(true);
    try {
      const { data } = await api.put(`/forms/${formId}/respuestas/${respuesta.id}/calificacion`, { por_pregunta: ediciones });
      setEstado((e) => ({ ...e, respuesta: data || e.respuesta }));
      setEdiciones({});
      toast.success('Calificación guardada');
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo guardar la calificación'));
    } finally {
      setGuardando(false);
    }
  };

  const publicar = async () => {
    if (!respuesta) return;
    if (hayCambios) await guardar();
    setPublicando(true);
    try {
      const { data } = await api.post(`/forms/${formId}/calificaciones/publicar`, { ids: [respuesta.id] });
      toast.success(data?.correos_enviados ? 'Calificación publicada y enviada por correo' : 'Calificación publicada');
      setRecarga((n) => n + 1);
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo publicar la calificación'));
    } finally {
      setPublicando(false);
    }
  };

  const borrar = async () => {
    setConfirmarBorrar(false);
    try {
      await api.delete(`/forms/${formId}/respuestas/${respuesta.id}`);
      toast.success('Respuesta eliminada');
      onBorrada?.();
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo eliminar la respuesta'));
    }
  };

  return (
    <div className="rs-individual">
      <div className="rs-tarjeta rs-paginador">
        <div className="rs-paginador-nav">
          <button type="button" className="od-tool-btn" onClick={() => ir(indice - 1)} disabled={indice <= 0} aria-label="Respuesta anterior">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <form
            className="rs-paginador-ir"
            onSubmit={(e) => {
              e.preventDefault();
              const n = parseInt(irA, 10);
              if (!Number.isNaN(n)) ir(n - 1);
              setIrA('');
            }}
          >
            <label className="sr-only" htmlFor="rs-ir-a">Ir a la respuesta</label>
            <input
              id="rs-ir-a"
              className="rs-paginador-input"
              inputMode="numeric"
              placeholder={String(indice + 1)}
              value={irA}
              onChange={(e) => setIrA(e.target.value.replace(/\D/g, ''))}
            />
            <span className="rs-paginador-de">de {total}</span>
          </form>
          <button type="button" className="od-tool-btn" onClick={() => ir(indice + 1)} disabled={indice >= total - 1} aria-label="Respuesta siguiente">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <div className="rs-paginador-acciones">
          <button type="button" className="od-tool-btn" onClick={() => respuesta && onImprimir?.([respuesta])} disabled={!respuesta} aria-label="Imprimir respuesta" title="Imprimir">
            <Printer className="h-[18px] w-[18px]" />
          </button>
          {puedeEditar && (
            <button type="button" className="od-tool-btn rs-peligro" onClick={() => setConfirmarBorrar(true)} disabled={!respuesta} aria-label="Eliminar respuesta" title="Eliminar">
              <Trash2 className="h-[18px] w-[18px]" />
            </button>
          )}
        </div>
      </div>

      {estado.cargando && <div className="rs-cargando"><Loader2 className="h-7 w-7 od-spin" aria-label="Cargando respuesta" /></div>}
      {estado.error && <p className="rs-vacio">{estado.error}</p>}

      {respuesta && !estado.cargando && (
        <>
          <div className="rs-tarjeta rs-respuesta-cabeza">
            <div>
              <div className="rs-respuesta-numero">Respuesta #{respuesta.numero}</div>
              <div className="rs-tarjeta-meta">
                Enviada {fechaLarga(respuesta.enviada_en)}
                {respuesta.actualizada_en && respuesta.actualizada_en !== respuesta.enviada_en ? ` · editada ${fechaLarga(respuesta.actualizada_en)}` : ''}
              </div>
              {respuesta.correo && <div className="rs-respuesta-correo">{respuesta.correo}</div>}
            </div>
            {es && (
              <div className="rs-respuesta-puntos">
                <div className="rs-respuesta-total">
                  <strong>{Math.round(totalPuntos * 100) / 100}</strong> / {maximo} puntos
                </div>
                {respuesta.puntuacion?.publicada
                  ? <span className="rs-chip-publicada">Calificación publicada</span>
                  : <span className="rs-chip-pendiente">Sin publicar</span>}
                {puedeEditar && (
                  <div className="rs-respuesta-botones">
                    {hayCambios && (
                      <button type="button" className="od-btn-secondary" onClick={guardar} disabled={guardando}>
                        {guardando && <Loader2 className="h-4 w-4 od-spin" />} Guardar
                      </button>
                    )}
                    {(!respuesta.puntuacion?.publicada || hayCambios) && (
                      <button type="button" className="od-btn-primary" onClick={publicar} disabled={publicando || guardando}>
                        {publicando ? <Loader2 className="h-4 w-4 od-spin" /> : <Send className="h-4 w-4" />} Publicar calificación
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="fm-raiz rs-lienzo-individual" style={vars}>
            <div className="fm-columna">
              {items.map((item) => {
                if (item.tipo === 'seccion') {
                  return (
                    <div key={item.id} className="fm-tarjeta fr-seccion">
                      <h2 className="fr-seccion-titulo">{item.titulo || 'Sección sin título'}</h2>
                    </div>
                  );
                }
                const resultado = porPregunta[item.id];
                // Una pregunta sin puntos (el nombre, el correo…) no se califica: ni insignia ni controles
                const pregunta = esPregunta(item) && (resultado?.maximo ?? maximoDe(item)) > 0;
                const edicion = ediciones[item.id];
                const insignia = es && pregunta ? {
                  ...(resultado || {}),
                  puntos: puntosDe(item, resultado, edicion),
                  maximo: resultado?.maximo ?? maximoDe(item),
                  correcta: edicion?.puntos !== undefined ? undefined : resultado?.correcta,
                } : null;
                return (
                  <TarjetaItem
                    key={item.id}
                    item={item}
                    valor={respuesta.respuestas?.[item.id]}
                    soloLectura
                    marcas={es && item.clave?.correctas ? { correctas: item.clave.correctas } : null}
                    enlaceArchivo={(a) => `${CORREO_URL}/file/${a.id}`}
                    encabezadoExtra={insignia ? <InsigniaPuntos resultado={insignia} /> : null}
                    pie={es && pregunta ? (
                      puedeEditar ? (
                        <CalificarRespuesta
                          item={item}
                          resultado={resultado}
                          edicion={edicion}
                          onCambio={editar(item.id)}
                          deshabilitado={guardando || publicando}
                        />
                      ) : <Retroalimentacion retro={resultado?.retro} />
                    ) : null}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}

      <Dialog open={irPendiente !== null} onOpenChange={(v) => !v && setIrPendiente(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>¿Descartar las calificaciones sin guardar?</DialogTitle>
            <DialogDescription>Cambiaste puntos o comentarios de esta respuesta y aún no los guardas.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIrPendiente(null)}>Seguir calificando</Button>
            <Button
              onClick={() => {
                const destino = irPendiente;
                setIrPendiente(null);
                ir(destino, true);
              }}
            >
              Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmarBorrar} onOpenChange={setConfirmarBorrar}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>¿Eliminar esta respuesta?</DialogTitle>
            <DialogDescription>
              La respuesta #{respuesta?.numero} se borrará para siempre. Los archivos que se subieron con ella se quedan en Drive.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmarBorrar(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={borrar}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
